import { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Flex } from '@theme-ui/components';
import { useQuery } from '@apollo/client';
import { css } from '@emotion/react';

import { useGetCartId } from '@app/context/CartIdProvider/CartIdProvider';
import { isNotNullish } from '@app/helpers/predicate';
import type {
  CartQuery_magento2,
  CartQuery_magento2_cart,
} from '@app/pages/Cart/__generated__/LegacyCartQueriesTypes';

import type { AppliedGiftCardsQuery } from '../CartGiftCard/__generated__/AppliedGiftCardsQuery';
import { APPLIED_GIFT_CARDS_QUERY } from '../CartGiftCard/CartGiftCard.queries';
import { useShippingAddressesQuery } from './__graphql__/CartTotals';
import messages from './CartTotals.messages';
import { GrandTotal, TaxInfo, Total } from './CartTotals.styles';

const AppliedGiftCards = () => {
  const { cartId } = useGetCartId();
  const { data, loading } = useQuery<AppliedGiftCardsQuery>(
    APPLIED_GIFT_CARDS_QUERY,
    {
      variables: { cartId },
      skip: !cartId,
    },
  );
  const appliedGiftCards = data?.magento2?.cart?.appliedGiftCards;

  const validAppliedGiftCards = useMemo(
    () =>
      appliedGiftCards
        ?.filter(isNotNullish)
        .filter((el) => Boolean(el.appliedBalance?.value)),
    [appliedGiftCards],
  );

  if (loading) return null;

  return (
    <>
      {validAppliedGiftCards?.map(({ appliedBalance, code }) => (
        <Total
          data-testid={`cart-totals-gift-card-${code}`}
          key={`gift-card-${code}`}
          label={
            <>
              <FormattedMessage {...messages.giftCard} /> (
              <span data-testid={`gift-card-${code}-in-totals`}>{code}</span>)
            </>
          }
          amount={{
            currency: appliedBalance?.currency,
            value: (appliedBalance?.value ?? 0) * -1,
          }}
        />
      )) ?? null}
    </>
  );
};

const ShippingAddress = ({
  includingTax,
  cartId,
}: {
  includingTax: boolean;
  cartId?: string;
}) => {
  const { data, loading } = useShippingAddressesQuery({
    variables: {
      cartId: cartId ?? '',
    },
    skip: !cartId,
  });

  const shippingAddresses = data?.magento2?.cart?.shippingAddresses;
  const selectedShippingMethod = shippingAddresses?.[0]?.selectedShippingMethod;

  if (loading || !cartId) return null;

  return (
    <>
      {shippingAddresses && selectedShippingMethod && (
        <Total
          data-testid="cart-totals-shipping-costs"
          key="shippingCosts"
          label={<FormattedMessage {...messages.shippingHandling} />}
          amount={
            selectedShippingMethod[includingTax ? 'amountInclTax' : 'amount']
          }
        />
      )}
    </>
  );
};

export interface CartTotalsProps {
  prices?: CartQuery_magento2_cart['prices'];
  storeConfig?: CartQuery_magento2['storeConfig'];
  cartId?: string;
}

const CartTotals = (props: CartTotalsProps) => {
  const { prices, storeConfig, cartId } = props;

  const {
    // Represented by "Subtotal"
    subtotalExcludingTax,
    subtotalIncludingTax,
    // Listed individually
    discounts,
    // Listed individually
    appliedTaxes,
    // Represented by Order Total
    grandTotal,
  } = prices ?? {};
  const validTaxes = useMemo(
    () => appliedTaxes?.filter(isNotNullish),
    [appliedTaxes],
  );
  const validDiscounts = useMemo(
    () => discounts?.filter(isNotNullish),
    [discounts],
  );

  const includingTax = storeConfig?.taxSalesDisplaySubtotal === 'including';

  return (
    <dl>
      {includingTax
        ? subtotalIncludingTax && (
            <Total
              data-testid="cart-totals-subtotal"
              key="subtotal"
              label={<FormattedMessage {...messages.subtotal} />}
              amount={subtotalIncludingTax}
            />
          )
        : subtotalExcludingTax && (
            <Total
              data-testid="cart-totals-subtotal"
              key="subtotal"
              label={<FormattedMessage {...messages.subtotal} />}
              amount={subtotalExcludingTax}
            />
          )}

      {validDiscounts?.map(({ label, amount }) => (
        <Total
          data-testid={`cart-totals-discount-${label}`}
          key={`discount-${label}`}
          label={<FormattedMessage {...messages.discountLabel} />}
          amount={{ ...amount, value: (amount?.value ?? 0) * -1 }}
        />
      )) ?? null}

      <AppliedGiftCards />

      {validTaxes?.map(
        ({ label, amount }) =>
          !includingTax && (
            <Flex
              key={label}
              css={css`
                margin-bottom: 1rem;
              `}
            >
              <Total
                data-testid={`cart-totals-tax-${label}`}
                label={
                  <>
                    <FormattedMessage {...messages.exTax} />
                    {label && ` (${label})`}
                  </>
                }
                amount={amount}
              />
              {'*'}
            </Flex>
          ),
      )}
      <ShippingAddress includingTax={includingTax} cartId={cartId} />
      {grandTotal && (
        <GrandTotal
          data-testid="cart-totals-grand-total"
          key="grandTotal"
          label={<FormattedMessage {...messages.grandTotal} />}
          amount={grandTotal}
        />
      )}

      {!includingTax && Boolean(validTaxes?.length) && (
        <TaxInfo>
          {'*'}
          <FormattedMessage {...messages.taxCheckout} />
        </TaxInfo>
      )}
    </dl>
  );
};

export default memo(CartTotals);
